import { createRouter, createWebHistory } from "vue-router";

const routes = [
  { path: "/login", name: "login", component: () => import("../views/LoginView.vue"), meta: { public: true } },
  { path: "/register", name: "register", component: () => import("../views/RegisterView.vue"), meta: { public: true } },
  { path: "/forgot", name: "forgot", component: () => import("../views/ForgotPasswordView.vue"), meta: { public: true } },
  { path: "/reset", name: "reset", component: () => import("../views/ResetPasswordView.vue"), meta: { public: true } },

  // Outside the site layout on purpose: no navigation, no wallet bar, nothing to
  // click past. A signed-in but unverified account can reach this and nothing else.
  { path: "/verify", name: "verify-email", component: () => import("../views/VerifyEmailView.vue") },
  {
    path: "/",
    // LayoutHost picks the visitor or player shell. Paths stay identical across
    // signing in, so a bookmarked lobby survives it.
    component: () => import("../layouts/LayoutHost.vue"),
    children: [
      // Browsable signed out. A lobby hidden behind a login converts far worse —
      // players want to see the games before creating an account.
      { path: "", name: "lobby", component: () => import("../views/LobbyView.vue"), meta: { public: true } },
      // Paths say what the page does; route NAMES stay as they were so every
      // internal { name: … } link keeps working. Old paths redirect, because
      // they are already live and in people's history.
      { path: "money", name: "cashier", component: () => import("../views/CashierView.vue") },

      // Balance and Bank account are tabs on the money page now. The route NAMES
      // stay so every { name: 'wallet' } / { name: 'verification' } link in the
      // app keeps resolving — and so do the paths, for links already sent out.
      {
        path: "balance",
        name: "wallet",
        redirect: () => ({ name: "cashier", query: { tab: "history" } }),
      },
      {
        path: "bank-account",
        name: "verification",
        redirect: () => ({ name: "cashier", query: { tab: "bank" } }),
      },

      { path: "wallet", redirect: () => ({ name: "cashier", query: { tab: "history" } }) },
      { path: "cashier", redirect: { name: "cashier" } },
      { path: "verification", redirect: () => ({ name: "cashier", query: { tab: "bank" } }) },
      // Public. It answers "how does this work" for somebody deciding whether
      // to sign up, which is the audience it exists for.
      {
        path: "help",
        name: "help",
        component: () => import("../views/HelpView.vue"),
        meta: { public: true },
      },
      { path: "promotions", name: "promotions", component: () => import("../views/PromotionsView.vue"), meta: { public: true } },
      { path: "account", name: "account", component: () => import("../views/AccountView.vue") },

      // Public, and reachable before an account exists — a player is told to
      // read these in order to register, so putting them behind the login is
      // backwards. One component, two documents, picked by meta.
      {
        path: "terms",
        name: "terms",
        component: () => import("../views/LegalView.vue"),
        meta: { public: true, doc: "terms" },
      },
      {
        path: "privacy",
        name: "privacy",
        component: () => import("../views/LegalView.vue"),
        meta: { public: true, doc: "privacy" },
      },

      // Public. The rate card and the VIP ladder are marketing — they are the
      // two pages that answer "is this worth joining", and putting them behind
      // the login means only people who already joined can read them.
      {
        path: "rebate",
        name: "rebate",
        component: () => import("../views/RebateView.vue"),
        meta: { public: true },
      },
      { path: "invite", name: "invite", component: () => import("../views/InviteView.vue") },
      {
        path: "vip",
        name: "vip",
        component: () => import("../views/VipView.vue"),
        meta: { public: true },
      },
      { path: "win-loss", name: "win-loss", component: () => import("../views/WinLossView.vue") },
    ],
  },
  { path: "/:pathMatch(.*)*", redirect: { name: "lobby" } },
];

const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach(async (to) => {
  const { useAuthStore } = await import("../api");
  const auth = useAuthStore();

  if (!auth.initialized) await auth.restore();

  // A signed-in account with an unconfirmed email goes nowhere but /verify.
  //
  // Checked before the public-route branch, so it also catches the lobby — the
  // point of a hard gate is that there is nothing else to click. The server is
  // still the authority: it independently refuses withdrawals from an unverified
  // account, so this is the funnel, not the control.
  if (auth.isAuthenticated && auth.user?.emailVerified === false && to.name !== "verify-email") {
    return { name: "verify-email" };
  }

  // Verified, or signed out — nothing to do on this screen.
  if (to.name === "verify-email" && auth.user?.emailVerified !== false) {
    return auth.isAuthenticated ? { name: "lobby" } : { name: "login" };
  }

  if (to.meta.public) {
    return auth.isAuthenticated && (to.name === "login" || to.name === "register")
      ? { name: "lobby" }
      : true;
  }

  if (!auth.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }

  return true;
});

export default router;
